package com.flowyride.data.speedcameras.flowyride

import com.flowyride.core.api.SpeedCameraDto
import com.flowyride.core.api.SpeedCamerasApi
import com.flowyride.core.api.toDomain
import com.flowyride.core.domain.speedcamera.SpeedCamera
import com.flowyride.core.domain.speedcamera.SpeedCameraRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.http.isSuccess
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

/**
 * The list kept by the FlowyRide server: light vehicles' limits (data.gouv gives the trucks' one on
 * discriminating cameras), one call instead of 17 pages. Cached once loaded.
 *
 * @param client a client carrying the session (see `withServerSession`).
 */
class FlowyRideServerSpeedCameraRepository(
    private val client: HttpClient,
    baseUrl: String,
) : SpeedCameraRepository {

    private val url = baseUrl.trimEnd('/') + SpeedCamerasApi.PATH
    private val mutex = Mutex()
    private var cache: List<SpeedCamera>? = null

    override suspend fun all(): List<SpeedCamera> = mutex.withLock {
        cache ?: download().also { cache = it }
    }

    private suspend fun download(): List<SpeedCamera> {
        val response = client.get(url)
        check(response.status.isSuccess()) { "Speed cameras: HTTP ${response.status.value}" }
        return response.body<List<SpeedCameraDto>>().mapNotNull { runCatching { it.toDomain() }.getOrNull() }
    }
}

/** [primary], or [fallback] when it fails (e.g. a server not updated yet). */
class FallbackSpeedCameraRepository(
    private val primary: SpeedCameraRepository,
    private val fallback: SpeedCameraRepository,
) : SpeedCameraRepository {
    override suspend fun all(): List<SpeedCamera> =
        try {
            primary.all()
        } catch (e: kotlin.coroutines.cancellation.CancellationException) {
            throw e
        } catch (_: Throwable) {
            // Throwable: on the web, Ktor reports network failures as kotlin.Error.
            fallback.all()
        }
}
